import React, { useState, useRef } from 'react';
import { Upload, File, X, CheckCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface DocumentUploadProps {
  title?: string;
  description?: string;
  acceptedTypes?: string;
  onUpload: (file: File) => Promise<void>;
}
export function DocumentUpload({
  title = 'Upload Document',
  description = 'Drag and drop your file here, or click to browse',
  acceptedTypes = '.pdf,.jpg,.png',
  onUpload
}: DocumentUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  const handleUploadClick = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      await onUpload(file);
      setUploadSuccess(true);
      setTimeout(() => {
        setFile(null);
        setUploadSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Upload failed', error);
    } finally {
      setIsUploading(false);
    }
  };
  return <div className="w-full" data-id="element-2689">
      <AnimatePresence mode="wait" data-id="element-2690">
        {!file ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} data-id="element-2691">
            <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-2692">
              <Upload className="h-6 w-6 text-blue-600" data-id="element-2693" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2694">{title}</h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2695">{description}</p>
            <p className="text-xs text-gray-400 uppercase tracking-wide" data-id="element-2696">
              Supports: {acceptedTypes.replace(/\./g, ' ').toUpperCase()}
            </p>
            <input ref={fileInputRef} type="file" className="hidden" accept={acceptedTypes} onChange={handleFileSelect} data-id="element-2697" />
          </motion.div> : <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-2698">
            <div className="flex items-center justify-between mb-6" data-id="element-2699">
              <div className="flex items-center" data-id="element-2700">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3" data-id="element-2701">
                  <File className="h-5 w-5 text-blue-600" data-id="element-2702" />
                </div>
                <div data-id="element-2703">
                  <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-2704">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2705">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              {!isUploading && !uploadSuccess && <button onClick={() => setFile(null)} className="text-gray-400 hover:text-gray-600" data-id="element-2706">
                  <X className="h-5 w-5" data-id="element-2707" />
                </button>}
            </div>

            {uploadSuccess ? <div className="flex items-center justify-center text-green-600 py-2" data-id="element-2708">
                <CheckCircle className="h-5 w-5 mr-2" data-id="element-2709" />
                <span className="font-medium" data-id="element-2710">Upload Complete!</span>
              </div> : <button onClick={handleUploadClick} disabled={isUploading} className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 transition-colors" data-id="element-2711">
                {isUploading ? <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-2712" />
                    Uploading...
                  </> : 'Confirm Upload'}
              </button>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}